import React, { createContext, useContext, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { en } from './en';
import { pl } from './pl';
import { Locale } from './types';

export type Lang = 'en' | 'pl';

interface LocaleState {
  locale: Locale;
  lang: Lang;
  setLang: (lang: Lang) => void;
  toggleLang: () => void;
}

const LocaleContext = createContext<LocaleState | null>(null);

export function detectLang(userLang?: string): Lang {
  if (userLang === 'en' || userLang === 'pl') return userLang;
  const saved = localStorage.getItem('broomwagon-lang');
  if (saved === 'en' || saved === 'pl') return saved;
  return navigator.language.toLowerCase().startsWith('pl') ? 'pl' : 'en';
}

export function LocaleProvider({ children, initialLang }: { children: React.ReactNode; initialLang?: string }) {
  const { lang: urlLang } = useParams<{ lang?: string }>();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const currentLang: Lang = (urlLang === 'pl' || urlLang === 'en') ? urlLang : detectLang(initialLang);

  useEffect(() => {
    // If we are at root "/" or have invalid lang in URL, redirect to detected lang
    if (urlLang !== 'en' && urlLang !== 'pl') {
      const targetLang = detectLang();
      // Remove leading slash for clean concatenation
      const cleanPath = pathname.replace(/^\//, ''); 
      // If the clean path is entirely empty or starts with an invalid language code
      const isPathEmpty = cleanPath === '' || cleanPath === '/';
      const newPath = isPathEmpty ? `/${targetLang}` : `/${targetLang}/${cleanPath}`;
      
      // Clean up double slashes
      navigate((newPath + search).replace(/\/\//g, '/'), { replace: true });
    } else {
      localStorage.setItem('broomwagon-lang', urlLang);
    }
  }, [urlLang, navigate, pathname, search]);

  const setLang = (next: Lang) => {
    const newPath = pathname.replace(/^\/(en|pl)/, `/${next}`);
    navigate(newPath + search);
  };

  const toggleLang = () => {
    setLang(currentLang === 'en' ? 'pl' : 'en');
  };

  const value: LocaleState = {
    locale: currentLang === 'pl' ? pl : en,
    lang: currentLang,
    setLang,
    toggleLang
  };

  return React.createElement(LocaleContext.Provider, { value }, children);
}

export function useLocaleContext(): LocaleState {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocaleContext used outside LocaleProvider');
  return ctx;
}
